import { Store, useStore } from '@betgames/bg-state-manager';
import { sessionStore } from '@bg-shared/business/Session/Session.store';
import { ILegacyOptionsConfig } from '@bg-shared/business/GamesOptions/interfaces';
import { mapLotteryOptions } from '@bg-shared/contexts/Games/utils/mapLotteryOptions';
import { ILotteryDraw } from '@bg-shared/interfaces/Draws/ILotteryDraw';
import { gamesStatusService } from '@bg-shared/business/GamesStatus';
import { GAME_STOP_BETTING_OFFSET } from '@bg-shared/constants/constants';
import { gamesRunsEntity } from '@bg-shared/business/GamesRuns';
import { gamesOptionsEntity } from '@bg-shared/business/GamesOptions';
import { StoresEntity } from '@bg-shared/business/StoreFeature';
import { keyBy } from 'lodash-unified';
import { ILotteryGameId } from '@bg-shared/interfaces/GameId';
import { mapToGroupsIds } from '@bg-shared/business/GamesOptions/utils/mapToGroupsIds';
import { IGameRouletteModel } from './interfaces';

export class GameRouletteStore extends Store<IGameRouletteModel> {
    constructor(private readonly gameId: ILotteryGameId) {
        super({
            key: `GameRoulette/${gameId}`,
            default: null,
        });
    }

    public init(draw: ILotteryDraw, bettingOptions: ILegacyOptionsConfig): void {
        gamesRunsEntity.store(this.gameId).update({
            run: {
                id: Number(draw.runId),
                code: draw.drawCode,
            },
        });

        gamesOptionsEntity.store(this.gameId).update({
            items: bettingOptions.items,
            options: keyBy(bettingOptions.groups.map((group) => group.options).flat(), 'id'),
            groups: mapToGroupsIds(bettingOptions.groups),
        });

        gamesStatusService.configureTimer({
            gameId: this.gameId,
            seconds: draw.secondsLeft,
            timestamp: draw.infoTime,
            offsetSeconds: GAME_STOP_BETTING_OFFSET,
        });

        this.update({
            data: {
                gameId: this.gameId,
                roundId: 0,
                runRoundId: 0,
                drawCode: draw.drawCode,
                runId: Number(draw.runId),
                time: draw.time,
                nextRunId: Number(draw.nextRunId),
                nextDrawCode: draw.nextDrawCode,
                bettingOptions: mapLotteryOptions({
                    gameId: this.gameId,
                    options: bettingOptions,
                    format: sessionStore.oddsFormat,
                }),
                gameItems: bettingOptions.items,
                infoTime: draw.infoTime,
                secondsLeft: draw.secondsLeft,
            },
        });
    }

    public updateGame(timestamp: number, data: ILotteryDraw): void {
        gamesRunsEntity.store(this.gameId).update({
            run: {
                id: Number(data.runId),
                code: data.drawCode,
            },
        });

        this.update((draft) => {
            draft.data.drawCode = data.drawCode;
            draft.data.runId = Number(data.runId);
            draft.data.nextDrawCode = data.nextDrawCode;
            draft.data.nextRunId = Number(data.nextRunId);
            draft.data.time = data.time;
            draft.data.infoTime = data.infoTime;
            draft.data.secondsLeft = data.secondsLeft;
        });
    }

    public useState = (
        () => () =>
            useStore(this)
    )();
}

export const gamesRouletteStoresEntity = new StoresEntity(GameRouletteStore);
